import _ from 'lodash';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';

import { IwDynamicFormattedMessage } from '../../../../iwDynamicFormattedMessage/IwDynamicFormattedMessage';
import { UploadSVG } from '../../../../svgComponents/uploadSVG';
import ConfirmationButton from '../../../ui/confirmationButton/ConfirmationButton';
import { LoadingSpinner } from '../../../ui/loadingSpinner';
import { Prompt } from '../../../ui/prompt';
import documentTypeMessages from '../chooseDocumentType/ChooseDocumentType.messages';
import { TDocument } from '../UploadDocumentsModal.reducer';
import { BANK_STATMENT_DOC_TYPE } from '../UploadDocumentsModalHelper';
import messages from './DocumentUploader.messages';
import styles from './DocumentUploader.module.css';

const MAX_FILE_SIZE = 1073741274; // 1GB
const ACCEPTED_FILE_TYPES = {
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'text/csv': ['.csv'],
    'image/jpeg': ['.jpeg', '.jpg'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
        '.xls'
    ],
    'application/msword': ['.docx']
};

type TProps = {
    closeModal: () => void;
    goBackToDocumentTypeSelection: () => void;
    uploadDocument: (
        documents: File[],
        documentType: { label: string; value: string },
        feedbackIds?: number[] | null,
        applicationId?: string
    ) => void;
    documentType: { label: string; value: string };
    documentData: { numberOfBankStatementsInMonths?: number };
    documentsStatus: TDocument[];
    preventBackNavigation?: boolean;
    feedbackIds?: number[] | null;
    applicationId?: string;
};

export const DocumentStatus = ({ document }: { document: TDocument }) => {
    if (document.pending) {
        return (
            <div className={styles.pendingStyle} data-testid="pending">
                <div className={styles.documentName}>
                    <FormattedMessage
                        {...messages.uploadingDocument}
                        values={{ document: document.name }}
                    />
                </div>
                <LoadingSpinner className={styles.uploadingDocuments} />
            </div>
        );
    }

    if (document.success) {
        return (
            <div className={styles.successStyle} data-testid="success">
                <div className={styles.documentName}>
                    <FormattedMessage
                        {...messages.uploadedDocument}
                        values={{ document: document.name }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.errorStyle} data-testid="error">
            <div className={styles.documentName}>
                <FormattedMessage
                    {...messages.failedDocument}
                    values={{ document: document.name }}
                />
            </div>
            <div className={styles.errorMessage}>
                <IwDynamicFormattedMessage
                    messages={messages}
                    messageKey={document.code!}
                />
            </div>
        </div>
    );
};

export const DocumentUploader = ({
    closeModal,
    goBackToDocumentTypeSelection,
    uploadDocument,
    documentType,
    documentData: { numberOfBankStatementsInMonths },
    documentsStatus,
    preventBackNavigation,
    feedbackIds,
    applicationId
}: TProps) => {
    const onDrop = (acceptedFiles: File[]) => {
        uploadDocument(acceptedFiles, documentType, feedbackIds, applicationId);
    };

    return (
        <div data-testid="upload-bank-statements-modal">
            <div className={styles.title} data-testid="upload-document-title">
                <FormattedMessage
                    {...messages.title}
                    values={{
                        documentType: (
                            <FormattedMessage
                                {...documentTypeMessages[
                                    documentType.label as keyof typeof documentTypeMessages
                                ]}
                            />
                        )
                    }}
                />
            </div>

            {_.isEqual(documentType, BANK_STATMENT_DOC_TYPE) && (
                <div>
                    <p data-testid="bank-statement-guidance">
                        {numberOfBankStatementsInMonths !== undefined ? (
                            <FormattedMessage
                                {...messages.bankStatementsForMonths}
                                values={{
                                    months: numberOfBankStatementsInMonths
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                {...messages.bankStatementGuidance}
                            />
                        )}
                    </p>
                </div>
            )}

            <div className={styles.uploadedFiles}>
                {documentsStatus.map((document, idx) => (
                    <DocumentStatus key={idx} document={document} />
                ))}
            </div>

            <Dropzone
                accept={ACCEPTED_FILE_TYPES}
                maxSize={MAX_FILE_SIZE}
                onDrop={onDrop}
            >
                {({ open, getRootProps, getInputProps }) => (
                    <div
                        className={styles.dropzone}
                        {...getRootProps({
                            onClick: (evt) => evt.stopPropagation()
                        })}
                    >
                        {/* eslint-disable-next-line iwoca/input-a11y */}
                        <input {...getInputProps()} />
                        <div className={styles.dropzoneContent}>
                            <UploadSVG />
                            <div className={styles.dropzoneMessage}>
                                <FormattedMessage
                                    {...messages.dragAndDropMessage}
                                />
                            </div>
                            <button
                                className={styles.browseFile}
                                onClick={() => open()}
                            >
                                <FormattedMessage {...messages.browseFile} />
                            </button>
                            {numberOfBankStatementsInMonths !== undefined && (
                                <ul className="mx-auto my-xl max-w-xl list-disc p-m pl-xl text-left text-s font-reg">
                                    <li>
                                        <FormattedMessage
                                            {...messages.acceptanceInfoOne}
                                        />
                                    </li>
                                    <li>
                                        <FormattedMessage
                                            {...messages.acceptanceInfoTwo}
                                        />
                                    </li>
                                    <li>
                                        <FormattedMessage
                                            {...messages.acceptanceInfoThree}
                                        />
                                    </li>
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </Dropzone>
            <Prompt className={styles.prompt}>
                {!preventBackNavigation && (
                    <button onClick={goBackToDocumentTypeSelection}>
                        <FormattedMessage {...messages.back} />
                    </button>
                )}
                <ConfirmationButton onClick={closeModal}>
                    <FormattedMessage {...messages.finish} />
                </ConfirmationButton>
            </Prompt>
        </div>
    );
};
